import React from 'react'
import { Box, Checkbox, FormControl, ListItemText, MenuItem, Select } from '@mui/material'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

const fieldSx = { marginRight: '20px', marginBottom: { md: '0', xs: '10px' } }

// แถบตัวกรอง — ครอบ field ทั้งหมด และกำหนดสีพื้นหลังของ field
export function FilterBar({ children }) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { md: 'row', xs: 'column' },
            justifyContent: { md: 'flex-end', xs: 'center' },
            flexWrap: 'wrap',
            alignItems: { md: 'center', xs: 'stretch' },
            '& .backgroundField': { background: '#a2c4e0' }
        }}>
            {children}
        </Box>
    )
}

// เลือกปีงบประมาณ (เลือกได้ปีเดียว)
export function YearSelect({ budgetYears, value, onChange }) {
    return (
        <Box className='backgroundField' sx={fieldSx}>
            <FormControl variant="standard" fullWidth>
                <Select
                    value={value || ''}
                    onChange={e => onChange(e.target.value)}
                    disableUnderline
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                >
                    {budgetYears?.map((x, i) => (
                        <MenuItem key={i} value={x?.BudgetYear}>{x?.BudgetYear}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

// ไตรมาสงบประมาณ (เลือกได้ตัวเดียว): Q1 ต.ค.-ธ.ค. / Q2 ม.ค.-มี.ค. / Q3 เม.ย.-มิ.ย. / Q4 ก.ค.-ก.ย.
const QUARTERS = [
    { value: '', label: 'ทุกไตรมาส' },
    { value: '1', label: 'ไตรมาส 1 (ต.ค.-ธ.ค.)' },
    { value: '2', label: 'ไตรมาส 2 (ม.ค.-มี.ค.)' },
    { value: '3', label: 'ไตรมาส 3 (เม.ย.-มิ.ย.)' },
    { value: '4', label: 'ไตรมาส 4 (ก.ค.-ก.ย.)' },
]

export function QuarterSelect({ value, onChange }) {
    return (
        <Box className='backgroundField' sx={fieldSx}>
            <FormControl variant="standard" sx={{ minWidth: 160 }}>
                <Select
                    value={value || ''}
                    displayEmpty
                    onChange={e => onChange(e.target.value)}
                    disableUnderline
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                >
                    {QUARTERS.map((q, i) => (
                        <MenuItem key={i} value={q.value}>{q.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

// เลือกหน่วยที่แสดง (เลือกได้ตัวเดียว): บาท (default) / กิโลกรัม
const UNITS = [
    { value: 'baht', label: 'บาท' },
    { value: 'kg', label: 'กิโลกรัม' },
]

export function UnitSelect({ value, onChange }) {
    return (
        <Box className='backgroundField' sx={fieldSx}>
            <FormControl variant="standard" sx={{ minWidth: 110 }}>
                <Select
                    value={value || 'baht'}
                    onChange={e => onChange(e.target.value)}
                    disableUnderline
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                >
                    {UNITS.map((u, i) => (
                        <MenuItem key={i} value={u.value}>{u.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

// เลือกหลายค่า (ศูนย์/พันธุ์) — จัดการ "ทั้งหมด" และ map ชื่อ→รหัสให้ parent
// options: array ของ { [idKey], [nameKey] }; selectedIds: array ของ id; onChange(ids)
export function MultiSelectFilter({ placeholder, allLabel, options, idKey, nameKey, selectedIds, onChange, width = 140 }) {
    const opts = options || []
    const selectedNames = opts.filter(o => (selectedIds || []).includes(o[idKey])).map(o => o[nameKey])

    const handleChange = (event) => {
        const value = event.target.value
        // คลิก "ทั้งหมด" ขณะเลือกครบแล้ว → ล้างค่า (ส่ง [] = ทั้งหมด)
        if (value?.length > opts.length) {
            onChange([])
            return
        }
        // คลิก "ทั้งหมด" ขณะยังไม่ครบ → เลือกทุกค่า
        if (value.filter(x => x === 'all').length > 0) {
            onChange(opts.map(o => o[idKey]))
            return
        }
        // เลือกรายตัว → map ชื่อกลับเป็นรหัส
        onChange(value.map(name => opts.find(o => o[nameKey] === name)?.[idKey]).filter(Boolean))
    }

    return (
        <Box className='backgroundField' sx={fieldSx}>
            <FormControl variant="standard" sx={{ width }}>
                <Select
                    value={selectedNames}
                    multiple
                    displayEmpty
                    onChange={handleChange}
                    // ว่าง = ทั้งหมด (ค่าเริ่มต้น) — แสดง allLabel ให้สื่อความชัด
                    renderValue={(selected) => selected.length === 0 ? allLabel : selected.join(', ')}
                    MenuProps={MenuProps}
                    disableUnderline
                >
                    <MenuItem value="all">
                        <Checkbox checked={(selectedIds || []).length === 0 || (opts.length > 0 && selectedIds.length === opts.length)} />
                        <ListItemText primary={allLabel} />
                    </MenuItem>
                    {opts.map((o, i) => (
                        <MenuItem key={i} value={o[nameKey]}>
                            <Checkbox checked={selectedNames.indexOf(o[nameKey]) > -1} />
                            <ListItemText primary={o[nameKey]} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}
